import { HOTELS_PRICE_TYPE } from '@skyscanner-internal/landing-pages-shared-constants/constants';

import HotelCard from '../HotelCard';
import HotelSearchResultsCaption from '../HotelSearchResultsCaption';
import { isEmptySearchResults, parseStayProp } from '../HotelsCommon/utils';
import SeoBannerSearchHotels from '../SeoBannerSearchHotels';
import { useMBDContext } from '../common/MBD/useMBDContext';

import ShimmerSearchResults from './ShimmerSearchResults';

import type { SearchResultShape } from '../../hotels-utils/types';
import type { HotelSearchResultsWrapperClientStrings } from '@skyscanner-internal/landing-pages-shared-types/types-shared/HotelSearchResultsWrapperProps';
import type { Stay } from '@skyscanner-internal/landing-pages-shared-types/types-shared/HotelStay';
import type { DestinationShape } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/types';

import STYLES from './HotelSearchResultsDisplay.module.scss';

type HotelSearchResultsDisplayProps = {
  stay: Stay;
  destination?: DestinationShape;
  hotelSearchResults?: SearchResultShape;
  fromSeoPage?: boolean;
  isMobile?: boolean;
  removePartnerRedirectLinks?: boolean;
  dayViewUrl?: string;
  clientStrings: HotelSearchResultsWrapperClientStrings;
  showReviewImage?: boolean;
  hotelCardsLimit?: number;
};

const DEFAULT_CARDS_NUMBER = 9;

const HotelCards = (
  hotelCards: any,
  stay: Stay,
  isMobile: boolean,
  clientStrings: HotelSearchResultsWrapperClientStrings,
  rawComponentName: string,
  buildMBDParam: (
    componentName: string,
    params?: Record<string, string>,
  ) => string,
  dayViewUrl?: string,
  destination?: DestinationShape,
  fromSeoPage?: boolean,
  removePartnerRedirectLinks?: boolean,
  showReviewImage?: boolean,
  hotelCardsLimit = DEFAULT_CARDS_NUMBER,
) =>
  hotelCards.slice(0, hotelCardsLimit).map((hotelCard: any, i: number) => {
    const itemComponentName = `${rawComponentName}_item`;
    const itemCtaComponentName = `${itemComponentName}_cta`;
    const commonParams = { hotel_id: String(hotelCard.id) };

    return (
      <div
        key={hotelCard.id}
        className={STYLES.HotelSearchResultsDisplay__card}
        data-tracking-element-id={`${itemComponentName}_${i}`}
        data-tracking-common-params={buildMBDParam(
          itemComponentName,
          commonParams,
        )}
      >
        <HotelCard
          {...hotelCard}
          {...hotelCard.priceInfo}
          stay={stay}
          isMobile={isMobile}
          priceType={HOTELS_PRICE_TYPE.PRICE_TYPE_PER_NIGHT}
          fromSeoPage={fromSeoPage}
          removePartnerRedirectLinks={removePartnerRedirectLinks}
          hotelCardAccessibility
          clientStrings={clientStrings}
          showReviewImage={showReviewImage}
          mbdCtaTracking={{
            elementId: `${itemCtaComponentName}_${i}`,
            commonParams: buildMBDParam(itemCtaComponentName, commonParams),
          }}
        />
        {i === 2 && destination && dayViewUrl && (
          <SeoBannerSearchHotels
            dayViewUrl={dayViewUrl}
            clientStrings={clientStrings}
          />
        )}
      </div>
    );
  });

const HotelSearchResultsDisplay = ({
  clientStrings,
  dayViewUrl = undefined,
  destination = undefined,
  fromSeoPage = undefined,
  hotelCardsLimit = DEFAULT_CARDS_NUMBER,
  hotelSearchResults = undefined,
  isMobile = false,
  removePartnerRedirectLinks = false,
  showReviewImage = undefined,
  stay,
}: HotelSearchResultsDisplayProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const {
    hotelCards = [],
    loading = true,
    pricePolicy,
  } = hotelSearchResults || {};
  const parsedStay = parseStayProp(stay);

  if (isEmptySearchResults(hotelSearchResults)) {
    return destination && dayViewUrl ? (
      <SeoBannerSearchHotels
        dayViewUrl={dayViewUrl}
        clientStrings={clientStrings}
      />
    ) : null;
  }

  return loading && hotelCards.length === 0 ? (
    <ShimmerSearchResults isMobile={isMobile} />
  ) : (
    <>
      {pricePolicy && (
        <div className={STYLES.HotelSearchResultsDisplay__totalPrice}>
          <HotelSearchResultsCaption
            stay={parsedStay}
            pricePolicy={pricePolicy}
          />
        </div>
      )}
      {HotelCards(
        hotelCards,
        parsedStay,
        isMobile,
        clientStrings,
        rawComponentName,
        buildMBDParam,
        dayViewUrl,
        destination,
        fromSeoPage,
        removePartnerRedirectLinks,
        showReviewImage,
        hotelCardsLimit,
      )}
    </>
  );
};

export default HotelSearchResultsDisplay;
